import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import ListItemText from '@mui/material/ListItemText'
import Chip from '@mui/material/Chip'
import Box from '@mui/material/Box'
import FormHelperText from '@mui/material/FormHelperText'

import { initializeTags } from '../../reducers/tagsReducers'

/**
 * TagSelector - Reusable tag selection component using MUI
 * Props:
 *   tagType: String (which tag type to load/select from, default 'default')
 *   selectedTags: Array of selected tag names
 *   onChange: Function (selectedTagNames) => void
 *   multiple: Boolean (allow multiple selection)
 *   label: String (optional label)
 *   required: Boolean (optional, default false)
 *   maxTags: Number (optional, for max allowed tags)
 *   onError: Function (optional, called with error state)
 *   forceShowErrors: Boolean (optional, default false) — when true, marks field as touched to show validation
 *   filterOption: Function (tag) => Boolean (optional) — when given, only tags it returns true for are offered as options
 *   getOptionHint: Function (tag) => String (optional) — secondary text under each option, exposed as its accessible description
 */
const TagSelector = ({
    tagType = 'default',
    selectedTags = [],
    onChange,
    multiple = true,
    label = 'Tags',
    required = false,
    maxTags = 5,
    onError,
    forceShowErrors = false,
    filterOption,
    getOptionHint,
}) => {
    const dispatch = useDispatch()
    const tagsByType = useSelector(state => state.tags)
    // Select tags for the specified type, sorted alphabetically, or empty array if not loaded
    const tagsForType = tagsByType?.[tagType]
    const tags = useMemo(
        () => tagsForType
            ? [...tagsForType]
                .filter(tag => (filterOption ? filterOption(tag) : true))
                .sort((a, b) => a.name.localeCompare(b.name))
            : [],
        [tagsForType, filterOption]
    )

    const [error, setError] = useState(false)
    const [helperText, setHelperText] = useState('')
    const [touched, setTouched] = useState(false)
    const prevForceShowErrorsRef = useRef(forceShowErrors)

    useEffect(() => {
        // Only fetch tags if this tagType hasn't been initialized at all.
        // If the tagType exists in state (even as an empty array) we won't re-fetch.
        if (tagsByType?.[tagType] === undefined) {
            dispatch(initializeTags(tagType))
        }
    }, [tagsByType, dispatch, tagType])

    useEffect(() => {
        let err = false
        let help = ''
        if (required && selectedTags.length === 0) {
            err = true
            help = 'At least one tag is required'
        } else if (maxTags && selectedTags.length > maxTags) {
            err = true
            help = `No more than ${maxTags} tags allowed`
        } else {
            help = `${selectedTags.length}/${maxTags} tags`
        }
        setError(err && touched)
        setHelperText(help)
        if (onError) onError(err && touched)
    }, [selectedTags, required, maxTags, touched, onError])

    useEffect(() => {
        if (forceShowErrors) {
            if (!touched) {
                setTouched(true)
            }
        } else if (prevForceShowErrorsRef.current && selectedTags.length === 0) {
            setTouched(false)
        }

        prevForceShowErrorsRef.current = forceShowErrors
    }, [forceShowErrors, touched, selectedTags.length])

    const handleChange = (event) => {
        const value = event.target.value;
        if (!touched) setTouched(true);
        const names = Array.isArray(value) ? value : [value];
        onChange(names);
    }

    const handleBlur = () => {
        if (!touched) setTouched(true)
    }

    return (
        <FormControl
            fullWidth
            variant="outlined"
            sx={{ marginY: 0 }}
            error={error}
            required={required}
        >
            <InputLabel>{label}</InputLabel>
            <Select
                key={`tags-${tagType}-${JSON.stringify(tags.map(t => t.name))}`}
                label={label}
                multiple={multiple}
                value={selectedTags}
                onChange={handleChange}
                onBlur={handleBlur}
                renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map(name => (
                            <Chip key={name} label={name} />
                        ))}
                    </Box>
                )}
            >
                {tags.map(tag => {
                    const hint = getOptionHint?.(tag)
                    const hintId = hint ? `${tagType}-option-hint-${tag.id}` : undefined
                    return (
                        <MenuItem
                            key={tag.id}
                            value={tag.name}
                            aria-label={hintId && tag.name}
                            aria-describedby={hintId}
                        >
                            {hintId
                                ? <ListItemText
                                    primary={tag.name}
                                    secondary={hint}
                                    secondaryTypographyProps={{ id: hintId }}
                                />
                                : tag.name}
                        </MenuItem>
                    )
                })}
            </Select>

            <FormHelperText>{helperText}</FormHelperText>
        </FormControl>
    )
}

export default TagSelector
